import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useOpenDocument } from '@/modules/document/hooks/use-open-document';

import type { UploadFileInChunksParams } from './types';

const CHUNK_SIZE = 4 * 1024 ** 2;
const MAX_UPLOAD_FILE_SIZE_MB = 100 * 1024 ** 2;

export const useUploadFileByChunks = () => {
	const { project, showToast } = useAppContext();
	const openDocument = useOpenDocument();
	const t = useTranslations();

	const uploadFileByChunks = async ({
		file,
		successCallback,
		onReset,
		uploadFunction,
		documentTypeName,
		region,
	}: UploadFileInChunksParams): Promise<void> => {
		if (file.size > MAX_UPLOAD_FILE_SIZE_MB) {
			return showToast({ message: t('T_FILE_SIZE_TOO_LARGE'), variant: 'error' });
		}
		let documentId: Data.Entity<'api::project-document.project-document'>['documentId'] | undefined;
		const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

		const uploadChunk = async (chunkIndex: number) => {
			const start = chunkIndex * CHUNK_SIZE;
			const end = Math.min((chunkIndex + 1) * CHUNK_SIZE, file.size);
			const chunk = file.slice(start, end);
			const formData = new FormData();

			formData.append('file', chunk);
			formData.append('fileSize', file.size.toString());
			formData.append('chunkIndex', chunkIndex.toString());
			formData.append('totalChunks', totalChunks.toString());
			formData.append('fileName', file.name);

			if (documentTypeName) {
				formData.append('documentTypeName', documentTypeName);

				if (documentTypeName === 'T_CURVES' && region) {
					formData.append('region', region);
				}
			}

			try {
				const response = await uploadFunction.mutateAsync({
					projectId: project?.documentId ?? '',
					body: formData,
				});
				const data = response.data as { projectDocument?: Data.Entity<'api::project-document.project-document'> };
				documentId = data.projectDocument?.documentId;
			} catch (error) {
				console.error('Error uploading file chunk', error);
				onReset();
			}
		};

		for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
			await uploadChunk(chunkIndex);

			if (chunkIndex === totalChunks - 1) {
				successCallback();
				onReset();
				showToast({ message: t('T_SUCCESS_DOCUMENT_FILE_UPLOADED'), variant: 'success' });

				if (documentId) {
					openDocument({
						documentId,
						documentTypeName,
						...(documentTypeName === 'T_CURVES' ? { region: 'IEC' } : {}),
					});
				}
			}
		}
	};

	return uploadFileByChunks;
};
